import { useEffect, useMemo, useState } from "react";
import { Button, Group, Modal, NumberInput, SegmentedControl, Select, Stack, Switch, Text, TextInput, Textarea } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import { apiErrorMessage } from "../../helpers/apiError";
import HttpClient from "../../helpers/HttpClient";
import { InventoryArticle } from "../../types/inventory.type";

type PersonOption = { id: number; firstName: string; lastName: string };

interface Props {
  opened: boolean;
  onClose: () => void;
  onSaved: () => void;
  // Pre-selected article (e.g. opened from the article page)
  articleId?: number;
}

// Reserve (pre-note, e.g. the squad bus for the weekend) or hand out right away.
export function RentalDialog({ opened, onClose, onSaved, articleId }: Props) {
  const [articles, setArticles] = useState<InventoryArticle[]>([]);
  const [persons, setPersons] = useState<PersonOption[]>([]);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [variantId, setVariantId] = useState<string | null>(null);
  const [renterMode, setRenterMode] = useState<"member" | "other">("member");
  const [personId, setPersonId] = useState<string | null>(null);
  const [renterName, setRenterName] = useState("");
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [quantity, setQuantity] = useState<number | string>(1);
  const [notes, setNotes] = useState("");
  const [checkOutNow, setCheckOutNow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opened) return;
    new HttpClient<InventoryArticle>("/inventory").getAll()
      .then((all) => setArticles(all.filter((a) => a.isRentable)))
      .catch(() => {});
    new HttpClient<PersonOption>("/person").getAll({ slim: "true" }).then(setPersons).catch(() => {});
    const now = dayjs().minute(0).second(0).millisecond(0).add(1, "hour");
    setSelectedArticleId(articleId ? String(articleId) : null);
    setVariantId(null);
    setRenterMode("member");
    setPersonId(null);
    setRenterName("");
    setFrom(now.toDate());
    setTo(now.add(1, "day").toDate());
    setQuantity(1);
    setNotes("");
    setCheckOutNow(false);
  }, [opened, articleId]);

  const article = articles.find((a) => String(a.id) === selectedArticleId) ?? null;
  const activeVariants = useMemo(() => (article?.variants ?? []).filter((v) => v.isActive), [article]);

  // Single-variant articles need no variant choice.
  useEffect(() => {
    if (activeVariants.length === 1) setVariantId(String(activeVariants[0].id));
    else if (!activeVariants.some((v) => String(v.id) === variantId)) setVariantId(null);
  }, [activeVariants, variantId]);

  const variant = activeVariants.find((v) => String(v.id) === variantId) ?? null;
  const fee = article?.rentalFee ? article.rentalFee * (Number(quantity) || 1) : 0;
  const renterOk = renterMode === "member" ? !!personId : renterName.trim().length > 0;
  const canSave = !!variant && renterOk && !!from && !!to && to > from && Number(quantity) >= 1;

  const save = async () => {
    if (!canSave || !variant) return;
    setSaving(true);
    try {
      await new HttpClient("/rental").post({
        variantId: variant.id,
        quantity: Number(quantity),
        personId: renterMode === "member" ? Number(personId) : null,
        renterName: renterMode === "other" ? renterName.trim() : null,
        plannedFrom: from!.toISOString(),
        plannedTo: to!.toISOString(),
        notes: notes.trim() || undefined,
        checkOutNow,
      } as never);
      notifications.show({ color: "green", message: checkOutNow ? "Ausgegeben." : "Reserviert." });
      onSaved();
      onClose();
    } catch (e) {
      notifications.show({ color: "red", message: apiErrorMessage(e, "Speichern fehlgeschlagen.") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Reservieren / Ausgeben" size="lg">
      <Stack>
        <Select
          label="Artikel"
          searchable
          data={articles.map((a) => ({ value: String(a.id), label: a.category ? `${a.name} (${a.category.name})` : a.name }))}
          value={selectedArticleId}
          onChange={setSelectedArticleId}
          nothingFoundMessage="Keine verleihbaren Artikel"
        />
        {activeVariants.length > 1 && (
          <Select
            label="Variante"
            data={activeVariants.map((v) => ({ value: String(v.id), label: `${v.label} – verfügbar: ${v.available}` }))}
            value={variantId}
            onChange={setVariantId}
          />
        )}
        <SegmentedControl
          value={renterMode}
          onChange={(v) => setRenterMode(v as "member" | "other")}
          data={[
            { value: "member", label: "Mitglied" },
            { value: "other", label: "Andere (Name)" },
          ]}
        />
        {renterMode === "member" ? (
          <Select
            label="Person"
            searchable
            data={persons.map((p) => ({ value: String(p.id), label: `${p.lastName} ${p.firstName}` }))}
            value={personId}
            onChange={setPersonId}
          />
        ) : (
          <TextInput
            label="Name"
            description="Ohne Mitgliedskonto wird keine Gebühr gebucht."
            value={renterName}
            onChange={(e) => setRenterName(e.currentTarget.value)}
          />
        )}
        <Group grow>
          <DateTimePicker label="Von" valueFormat="DD.MM.YYYY HH:mm" value={from} onChange={(v) => setFrom(v ? new Date(v) : null)} />
          <DateTimePicker label="Bis" valueFormat="DD.MM.YYYY HH:mm" value={to} onChange={(v) => setTo(v ? new Date(v) : null)} />
        </Group>
        <NumberInput label="Menge" value={quantity} onChange={setQuantity} min={1} allowDecimal={false} />
        <Textarea label="Notiz" autosize minRows={1} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
        <Switch
          label="Sofort ausgeben"
          description="Sonst wird der Artikel nur reserviert und später ausgegeben."
          checked={checkOutNow}
          onChange={(e) => setCheckOutNow(e.currentTarget.checked)}
        />
        {fee > 0 && renterMode === "member" && (
          <Text size="sm">
            Bei Ausgabe werden <b>{fee.toFixed(2).replace(".", ",")} €</b> dem Konto belastet
            {article?.rentalFeeMode === "deposit" ? " (Kaution, wird bei Rückgabe erstattet)." : " (Leihgebühr)."}
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} loading={saving} disabled={!canSave}>
            {checkOutNow ? "Ausgeben" : "Reservieren"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
